import { navigate } from 'gatsby';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import { CurationStatusBadge } from '@components/CurationStatusBadge';
import MuiDataTable from '@components/DataTable/MuiDataTable/MuiDataTable';
import NullStateDiv from '@components/NullStateDiv';
import SelfReportedPill from '@components/SelfReportedPill/SelfReportedPill';

import { useGetAllResourceModels } from '@hooks/api/patient-record';

import { formatFhirDate } from '@utils/fhir';

import type { CurationStatus } from 'generated/types';

const messages = defineMessages({
  noRecords: {
    id: 'clientProfile.noRecords',
    defaultMessage: 'NO {type}',
  },
});

function RecordInformationList({
  type = '',
  status,
}: {
  type: string;
  status: CurationStatus;
}) {
  const { formatMessage } = useIntl();
  const { data: records, loading } = useGetAllResourceModels({
    status,
  });
  const data = records?.getAllResourceModels ?? [];

  return (
    <ContentLoader loading={loading}>
      {data.length === 0 ? (
        <NullStateDiv>
          {formatMessage(messages.noRecords, { type })}
        </NullStateDiv>
      ) : (
        <MuiDataTable
          columns={[
            {
              name: 'status',
              label: 'Curation Status',
              className: 'small',
              options: {
                customBodyRender: (status: CurationStatus) => (
                  <CurationStatusBadge status={status} />
                ),
              },
            },
            {
              name: 'date',
              className: 'date',
              label: 'Date',
              options: {
                filter: true,
                sort: true,
                customBodyRender: (value: string) => formatFhirDate(value),
              },
            },
            {
              name: 'recordType',
              className: 'small',
              label: 'Record Type',
              options: {
                filter: true,
                sort: true,
                searchable: true,
              },
            },
            {
              name: 'displayName',
              className: 'small',
              label: 'Name',
              options: {
                sort: true,
                searchable: true,
              },
            },
            {
              name: 'selfReported',
              options: {
                customHeadLabelRender: () => null,
                customBodyRender: (value: boolean) => (
                  <SelfReportedPill value={value} />
                ),
              },
            },
          ]}
          data={data}
          options={{
            search: true,
            sort: true,
            filter: true,
            onRowClick: (_rowData, { dataIndex }) => {
              if (dataIndex <= data.length - 1) {
                const row = data[dataIndex];
                navigate(row.route ?? '');
              }
            },
          }}
        />
      )}
    </ContentLoader>
  );
}

export default RecordInformationList;
